import { useState } from 'react'

import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormattedNumber } from 'react-intl'
import { styled } from 'styled-components'

import {
  CurrencyStringCents,
  CurrencyStringDollars,
  FormattedMessage,
  InteractiveIcon,
} from '@b-stock/fe-core/bstock-react'
import { Colors, pxToRem, Typography } from '@b-stock/fe-core/design-system'

import type { TlcVatInfo } from '@/buyer-portal/components/features/listing/MaxVatExposureDisplay/getTlcVatInfo'
import type { TaxComponentBreakdown } from '@/queries/canadianTaxComponentBreakdown.types'

const Container = styled.div`
  border: ${pxToRem(1)} solid ${Colors.Semantic.Neutral.medium_grey};
  border-radius: 0.5rem;
  overflow: hidden;
`

const SummaryRow = styled.button`
  all: unset;
  align-items: center;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  width: 100%;
`

const SummaryLeft = styled.div`
  display: flex;
  align-items: center;
`

const SummaryLabel = styled.span`
  ${Typography.Subtitle2}
  color: ${Colors.Semantic.Neutral.black};
`

const InfoIconWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`

// Icon glyph, not text -- exempt from the 14px text-minimum floor (BP-2734 AC #1/#3).
const InfoIcon = styled(InteractiveIcon)`
  color: ${Colors.Semantic.Neutral.dark_grey};
  font-size: ${pxToRem(10)};
  pointer-events: initial;
`

const SummaryRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const TotalValue = styled.span`
  ${Typography.Title3}
  color: ${Colors.Semantic.Neutral.black};
  font-family: 'Proxima Nova';
`

// Icon glyph, not text -- exempt from the 14px text-minimum floor (BP-2734 AC #1/#3).
const ChevronIcon = styled(FontAwesomeIcon)`
  color: ${Colors.Semantic.Neutral.dark_grey};
  font-size: 0.75rem;
`

const BreakdownList = styled.div`
  border-top: ${pxToRem(1)} solid ${Colors.Semantic.Neutral.medium_grey};
  display: flex;
  flex-direction: column;
  padding: 0 0 0.75rem;
`

const LineItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  position: relative;

  &:not(:first-child)::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: ${pxToRem(1)};
    background: ${Colors.Semantic.Neutral.medium_grey};
  }
`

const TotalLineItem = styled(LineItem)`
  background: ${Colors.Semantic.Success.background};
  border-radius: 0.5rem;
  margin: 0 0.75rem;

  &:not(:first-child)::before {
    display: none;
  }
`

const LineLabel = styled.span`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
`

const LineValue = styled.span`
  ${Typography.Subtitle1}
  color: ${Colors.Semantic.Neutral.black};
`

const TotalLineLabel = styled(LineLabel)`
  font-weight: 600;
  color: ${Colors.Semantic.Success.copy};
`

const TotalLineValue = styled(LineValue)`
  color: ${Colors.Semantic.Success.copy};
`

const UnavailableText = styled.span`
  ${Typography.Body2}
  color: ${Colors.Semantic.Neutral.dark_grey};
`

const ns = 'Listing.actionColumn.actionCard'

type TlcBreakdownProps = {
  basis: number
  bstockFee: number
  feePercent: number
  shippingCents: number
  isWithoutShipping: boolean
  /**
   * B-Stock collects no freight on this lot, so it has no figure to show —
   * distinct from `isWithoutShipping`, which means no quote is selected yet.
   */
  suppressShippingLine?: boolean
  /**
   * The selected quote has no rate (a parcel zero-quote), so neither shipping
   * nor the total has a figure to show.
   */
  isShippingUnavailable?: boolean
  totalLandedCostWithTax: number
  onToggle?: () => void
  currency?: string
  defaultExpanded?: boolean
  amountLabelId?: string // Default: "If you bid"; overrideable for offers
  tooltipId?: string // Default: bid-worded tooltip; overrideable for offers
  tooltipWithTaxId?: string // Same, for the taxInfo-present variant
  tooltipWithVatId?: string // Same, for the vatInfo-present variant
  // FP-2498 — per-tax-type breakdown from svc/listing running estimate
  // (only populated for CAD listings). When present, an expandable
  // "Total Tax" row renders after Estimated Shipping.
  //
  // Grouped so `components` and `totalCents` are co-provided or absent
  // together — prevents a caller supplying components without a total
  // from silently rendering a $0 Total Tax header alongside non-zero
  // detail rows.
  taxInfo?: { components: TaxComponentBreakdown[]; totalCents: number }
  // FP-3262 — EU/GB VAT from svc/listing max-exposure (EUR/GBP listings).
  // When present, an "Estimated VAT" row renders after Estimated Shipping.
  vatInfo?: TlcVatInfo
}

export const TlcBreakdown = ({
  basis,
  bstockFee,
  feePercent,
  shippingCents,
  isWithoutShipping,
  suppressShippingLine = false,
  isShippingUnavailable = false,
  totalLandedCostWithTax,
  onToggle,
  currency = 'USD',
  amountLabelId = `${ns}.tlcBreakdown.bidAmount`,
  defaultExpanded = false,
  tooltipId = `${ns}.tlcBreakdown.tooltip`,
  tooltipWithTaxId = `${ns}.tlcBreakdown.tooltipWithTax`,
  tooltipWithVatId = `${ns}.tlcBreakdown.tooltipWithVat`,
  taxInfo,
  vatInfo,
}: TlcBreakdownProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const total = isShippingUnavailable ? (
    <UnavailableText>—</UnavailableText>
  ) : (
    <CurrencyStringDollars value={totalLandedCostWithTax} currency={currency} />
  )
  const showTax = !!taxInfo && taxInfo.components.length > 0
  const activeTooltipId = showTax
    ? tooltipWithTaxId
    : vatInfo
      ? tooltipWithVatId
      : tooltipId

  return (
    <Container>
      <SummaryRow
        type="button"
        onClick={() => {
          onToggle?.()
          setIsExpanded((v) => !v)
        }}
        aria-expanded={isExpanded}
      >
        <SummaryLeft>
          <SummaryLabel>
            <FormattedMessage id={`${ns}.tlcBreakdown.label`} />
          </SummaryLabel>
          <InfoIconWrapper>
            <InfoIcon
              size="small"
              tooltipProps={{ placement: 'top', hoverable: true }}
            >
              <FormattedMessage id={activeTooltipId} />
            </InfoIcon>
          </InfoIconWrapper>
        </SummaryLeft>
        <SummaryRight>
          {!isExpanded && <TotalValue>{total}</TotalValue>}
          <ChevronIcon icon={isExpanded ? faChevronUp : faChevronDown} />
        </SummaryRight>
      </SummaryRow>

      {isExpanded && (
        <BreakdownList>
          <LineItem>
            <LineLabel>
              <FormattedMessage
                id={`${ns}.tlcBreakdown.bstockFee`}
                values={{ percent: feePercent }}
              />
            </LineLabel>
            <LineValue>
              <CurrencyStringDollars
                value={bstockFee / 100}
                currency={currency}
              />
            </LineValue>
          </LineItem>

          <LineItem>
            <LineLabel>
              <FormattedMessage id={amountLabelId} />
            </LineLabel>
            <LineValue>
              <CurrencyStringDollars value={basis} currency={currency} />
            </LineValue>
          </LineItem>

          <LineItem>
            <LineLabel>
              <FormattedMessage id={`${ns}.tlcBreakdown.estimatedShipping`} />
            </LineLabel>
            <LineValue>
              {isWithoutShipping ||
              suppressShippingLine ||
              isShippingUnavailable ? (
                <UnavailableText>—</UnavailableText>
              ) : (
                <CurrencyStringDollars
                  value={shippingCents / 100}
                  currency={currency}
                />
              )}
            </LineValue>
          </LineItem>

          {showTax && (
            <TaxTotalRow
              components={taxInfo.components}
              totalCents={taxInfo.totalCents}
              currency={currency}
            />
          )}

          {vatInfo && <VatRow vatInfo={vatInfo} currency={currency} />}

          <TotalLineItem>
            <TotalLineLabel>
              <FormattedMessage id={`${ns}.tlcBreakdown.total`} />
            </TotalLineLabel>
            <TotalLineValue>{total}</TotalLineValue>
          </TotalLineItem>
        </BreakdownList>
      )}
    </Container>
  )
}

/**
 * FP-3262 — "Estimated VAT" row for EU/GB listings. Unavailable renders
 * the label with a dash, never €0.00 (FR-020); reverse charge renders a
 * real 0 with its legend (FR-021).
 */
const VatRow = ({
  vatInfo,
  currency,
}: {
  vatInfo: TlcVatInfo
  currency: string
}) => (
  <>
    <LineItem data-testid="tlc-breakdown-vat">
      <LineLabel>
        {vatInfo.estimateUnavailable ? (
          <FormattedMessage id={`${ns}.tlcBreakdown.vat.unavailable`} />
        ) : (
          <FormattedMessage
            id={`${ns}.tlcBreakdown.vat.label`}
            values={{
              rate: (
                <FormattedNumber
                  value={vatInfo.rate}
                  style="percent"
                  maximumFractionDigits={3}
                />
              ),
            }}
          />
        )}
      </LineLabel>
      <LineValue>
        {vatInfo.estimateUnavailable ? (
          <UnavailableText>—</UnavailableText>
        ) : (
          <CurrencyStringCents
            value={vatInfo.amountCents}
            currency={currency}
          />
        )}
      </LineValue>
    </LineItem>
    {vatInfo.legend && (
      <VatLegend data-testid="tlc-breakdown-vat-legend">
        {vatInfo.legend}
      </VatLegend>
    )}
  </>
)

const VatLegend = styled.span`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
  padding: 0 1rem 0.5rem;
`

/**
 * FP-2498 — expandable "Total Tax" row inside TLC breakdown. All rows
 * render even when their amount is $0 per AC. Nested Toggle: outer row
 * shows summary + chevron; on expand, per-(component,taxType) rows appear.
 */
const TaxTotalRow = ({
  components,
  totalCents,
  currency,
}: {
  components: TaxComponentBreakdown[]
  totalCents: number
  currency: string
}) => {
  const [isOpen, setIsOpen] = useState(false)
  return (
    <>
      <TaxSummaryRow
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        data-testid="tlc-breakdown-tax-toggle"
      >
        <LineLabel>
          <FormattedMessage id={`${ns}.tlcBreakdown.totalTax`} />
        </LineLabel>
        <TaxSummaryRight>
          <LineValue>
            <CurrencyStringCents value={totalCents} currency={currency} />
          </LineValue>
          <ChevronIcon icon={isOpen ? faChevronUp : faChevronDown} />
        </TaxSummaryRight>
      </TaxSummaryRow>
      {isOpen && (
        <TaxDetailList data-testid="tlc-breakdown-tax-detail">
          {components.flatMap((c) =>
            c.lines.map((l) => (
              <TaxDetailRow key={`${c.component}-${l.taxType}`}>
                <TaxDetailLabel>
                  <FormattedMessage
                    id={`${ns}.tlcBreakdown.tax.line.${c.component}.${l.taxType}`}
                  />
                </TaxDetailLabel>
                <TaxDetailValue>
                  <CurrencyStringCents value={l.amount} currency={currency} />
                </TaxDetailValue>
              </TaxDetailRow>
            ))
          )}
        </TaxDetailList>
      )}
    </>
  )
}

const TaxSummaryRow = styled.button`
  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: ${pxToRem(1)};
    background: ${Colors.Semantic.Neutral.medium_grey};
  }
`

const TaxSummaryRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const TaxDetailList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 1rem 0.5rem 1.75rem;
`

const TaxDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.25rem 0;
`

const TaxDetailLabel = styled.span`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
`

const TaxDetailValue = styled.span`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
`
